import React, { useState } from 'react';
import { Check, Trash2, RotateCcw, X, Lock } from 'react-feather';
import { InlineSelect, InlineInput, InlineCheckbox, StatusDot } from '../../components/InlineTable';
import { ToolbarButton, ConfirmPopover } from '../../components/ConfigPages';
import { accountStatus, isDeletedRow, manualFieldsOf, lockSummary, MANUAL_FIELD_LABELS } from './coaShared';

/** Segmented GL account number: dimmed prefix/suffix around the bold base. */
export const AccountNumber = ({ account }) => (
  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
    {account.accountPrefix != null && account.accountPrefix !== '' && (
      <span style={{ color: 'var(--lodge-onyx-300)' }}>{account.accountPrefix}–</span>
    )}
    <span style={{ color: 'var(--lodge-onyx-800)', fontWeight: 600 }}>{account.accountNumber}</span>
    {account.accountSuffix != null && account.accountSuffix !== '' && (
      <span style={{ color: 'var(--lodge-onyx-300)' }}>–{account.accountSuffix}</span>
    )}
  </span>
);

/** Column widths shared by header, add row and data rows. */
export const COL = {
  check: { width: 44 },
  number: (hasSegments) => ({ width: hasSegments ? 150 : 110 }),
  name: { minWidth: 240 },
  type: { minWidth: 140 },
  group: { minWidth: 190 },
  dept: { minWidth: 210 },
  sub: { minWidth: 190 },
  actions: { width: 112 },
  status: { width: 44, textAlign: 'center' },
};

/**
 * One account row. Delete/restore live in a `cp-row-actions` cell with an
 * inline ConfirmPopover; deleted rows are read-only until restored.
 * A row with hand-set fields (manualFields) shows a lock that stays visible;
 * clicking it offers to release the lock so the accounting sync manages the
 * row again.
 */
export const AccountRow = ({
  account,
  selected,
  hasSegments,
  landsIn,
  accountTypeOptions,
  accountGroupOptions,
  deptOptions,
  subDeptOptionsFor,
  onCreateAccountGroup,
  onCreateDepartment,
  onCreateSubDepartment,
  onToggle,
  onField,
  onDelete,
  onRestore,
  onReleaseLock,
}) => {
  const [hov, setHov] = useState(false);
  const [confirm, setConfirm] = useState(null); // 'delete' | 'restore' | 'release' | null
  const [busy, setBusy] = useState(false);
  const deleted = isDeletedRow(account);
  const act = (hov || selected) && !deleted;
  const status = accountStatus(account);
  const locked = manualFieldsOf(account);
  const lockTitle = lockSummary(account);

  const run = async (fn) => {
    setBusy(true);
    try { await fn(account); setConfirm(null); } finally { setBusy(false); }
  };

  const rowCls = `cp-row${selected ? ' it-row-selected' : ''}${deleted ? ' it-row-deleted' : ''}`;

  return (
    <tr
      className={rowCls}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{ opacity: account.isOptimistic ? 0.55 : 1 }}
    >
      <td className="cp-cell" style={COL.check}>
        <InlineCheckbox checked={selected} onChange={() => onToggle(account.id)} disabled={deleted} />
      </td>
      <td className="cp-cell" style={COL.number(hasSegments)}>
        <AccountNumber account={account} />
      </td>
      <td style={COL.name}>
        <InlineInput
          value={account.accountName || ''}
          onChange={(v) => onField(account.id, 'accountName', v)}
          placeholder="Account name…"
          rowActive={act}
          disabled={deleted}
          ariaLabel="Account name"
        />
      </td>
      <td style={COL.type}>
        <InlineSelect value={account.accountType || null} options={accountTypeOptions} onChange={(v) => onField(account.id, 'accountType', v)} placeholder="Type…" rowActive={act} disabled={deleted} clearable={false} minWidth={150} ariaLabel="Account type" />
      </td>
      <td style={COL.group}>
        <InlineSelect value={account.accountGroup || null} options={accountGroupOptions} onChange={(v) => onField(account.id, 'accountGroup', v)} onCreateOption={onCreateAccountGroup} createLabel="Create group" placeholder="Group…" rowActive={act} disabled={deleted} clearable={false} minWidth={200} ariaLabel="Account group" />
      </td>
      <td style={COL.dept}>
        <InlineSelect value={account.department || null} options={deptOptions} onChange={(v) => onField(account.id, 'department', v ?? '')} onCreateOption={(name) => onCreateDepartment(name, account)} createLabel="Create department" placeholder="Department…" rowActive={act} disabled={deleted} minWidth={200} ariaLabel="Department" />
        <div className="cp-lands" style={{ minHeight: 15 }}>
          {landsIn ? <>lands in <strong>{landsIn}</strong></> : ' '}
        </div>
      </td>
      <td style={COL.sub}>
        <InlineSelect value={account.subDepartment || null} options={subDeptOptionsFor(account)} onChange={(v) => onField(account.id, 'subDepartment', v ?? '')} onCreateOption={(name) => onCreateSubDepartment(name, account)} createLabel="Create sub-department" placeholder="Sub-department…" rowActive={act} disabled={deleted} minWidth={200} ariaLabel="Sub-department" />
      </td>
      <td className="cp-cell-tight" style={COL.actions}>
        <div style={{ position: 'relative', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
          {locked.length > 0 && (
            <ToolbarButton
              small
              iconOnly
              variant="ghost"
              icon={Lock}
              title={`${lockTitle} Click to release.`}
              aria-label={`Set by hand: ${locked.map((f) => MANUAL_FIELD_LABELS[f]).join(', ')}. Release lock`}
              onClick={() => setConfirm('release')}
              disabled={busy || account.isOptimistic || deleted || !onReleaseLock}
              style={{ color: 'var(--lodge-orange-600)' }}
            />
          )}
          <div className="cp-row-actions" style={confirm ? { opacity: 1 } : undefined}>
            {deleted ? (
              <ToolbarButton small iconOnly variant="ghost" icon={RotateCcw} title="Restore account" aria-label="Restore account" onClick={() => setConfirm('restore')} disabled={busy || account.isOptimistic} />
            ) : (
              <ToolbarButton small iconOnly variant="ghost" icon={Trash2} title="Delete account" aria-label="Delete account" onClick={() => setConfirm('delete')} disabled={busy || account.isOptimistic} />
            )}
          </div>
          <ConfirmPopover
            open={confirm === 'delete'}
            onClose={() => setConfirm(null)}
            onConfirm={() => run(onDelete)}
            title={`Delete ${account.accountNumber}?`}
            body="The account is hidden from the chart and reports. You can restore it from Show deleted."
            confirmLabel="Delete"
            busy={busy}
          />
          <ConfirmPopover
            open={confirm === 'restore'}
            onClose={() => setConfirm(null)}
            onConfirm={() => run(onRestore)}
            title={`Restore ${account.accountNumber}?`}
            body="The account returns to the chart with its previous department and group."
            confirmLabel="Restore"
            danger={false}
            busy={busy}
          />
          <ConfirmPopover
            open={confirm === 'release'}
            onClose={() => setConfirm(null)}
            onConfirm={() => run(onReleaseLock)}
            title={`Release ${account.accountNumber}?`}
            body={`${locked.map((f) => MANUAL_FIELD_LABELS[f]).join(', ')} ${locked.length === 1 ? 'was' : 'were'} set by hand. Releasing lets the accounting sync overwrite them on its next run.`}
            confirmLabel="Release"
            danger={false}
            busy={busy}
          />
        </div>
      </td>
      <td style={COL.status}>
        <StatusDot state={status.state} title={status.title} />
      </td>
    </tr>
  );
};

/** Inline "new account" row at the top of the table. */
export const AddAccountRow = ({
  draft,
  setDraft,
  hasSegments,
  accountTypeOptions,
  accountGroupOptions,
  deptOptions,
  subDeptOptionsFor,
  onCreateAccountGroup,
  onCreateDepartment,
  onCreateSubDepartment,
  onSave,
  onCancel,
  saving,
}) => (
  <tr className="cp-row cp-row-new">
    <td className="cp-cell" style={COL.check} />
    <td style={COL.number(hasSegments)}>
      <InlineInput value={draft.accountNumber} onChange={(v) => setDraft((d) => ({ ...d, accountNumber: v }))} placeholder="Number…" rowActive mono debounceMs={0} ariaLabel="New account number" />
    </td>
    <td style={COL.name}>
      <InlineInput value={draft.accountName} onChange={(v) => setDraft((d) => ({ ...d, accountName: v }))} placeholder="Account name…" rowActive debounceMs={0} ariaLabel="New account name" />
    </td>
    <td style={COL.type}>
      <InlineSelect value={draft.accountType || null} options={accountTypeOptions} onChange={(v) => setDraft((d) => ({ ...d, accountType: v ?? '' }))} placeholder="Type…" rowActive clearable={false} minWidth={150} ariaLabel="New account type" />
    </td>
    <td style={COL.group}>
      <InlineSelect value={draft.accountGroup || null} options={accountGroupOptions} onChange={(v) => setDraft((d) => ({ ...d, accountGroup: v ?? '' }))} onCreateOption={onCreateAccountGroup} createLabel="Create group" placeholder="Group…" rowActive clearable={false} minWidth={200} ariaLabel="New account group" />
    </td>
    <td style={COL.dept}>
      <InlineSelect value={draft.department || null} options={deptOptions} onChange={(v) => setDraft((d) => ({ ...d, department: v ?? '', subDepartment: '' }))} onCreateOption={(name) => onCreateDepartment(name, draft)} createLabel="Create department" placeholder="Department…" rowActive minWidth={200} ariaLabel="New department" />
    </td>
    <td style={COL.sub}>
      <InlineSelect value={draft.subDepartment || null} options={subDeptOptionsFor(draft)} onChange={(v) => setDraft((d) => ({ ...d, subDepartment: v ?? '' }))} onCreateOption={(name) => onCreateSubDepartment(name, draft)} createLabel="Create sub-department" placeholder="Sub-department…" rowActive minWidth={200} ariaLabel="New sub-department" />
    </td>
    <td className="cp-cell-tight" style={COL.actions}>
      <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <ToolbarButton small variant="primary" icon={Check} onClick={onSave} disabled={saving} title="Save account">
          {saving ? 'Saving…' : 'Save'}
        </ToolbarButton>
        <ToolbarButton small iconOnly variant="ghost" icon={X} onClick={onCancel} disabled={saving} title="Cancel" aria-label="Cancel" />
      </div>
    </td>
    <td style={COL.status} />
  </tr>
);
